import { useParams } from 'react-router-dom';
import { useAsyncEntity } from '@backstage/plugin-catalog-react';
import { makeStyles } from 'tss-react/mui';
import {
  getColors,
  PageBreadcrumbs,
  typography,
} from '@mercedes-benz/odp-theme-v2';
import { SystemDetailsPage } from '../system-details/SystemDetailsPage';
import { AcademyEntityPage } from './AcademyEntityPage';
import { ApiEntityPage } from './ApiEntityPage';
import { ComponentEntityPage } from './ComponentEntityPage';
import { ContentEntityPage } from './ContentEntityPage';
import { DomainEntityPage } from './DomainEntityPage';
import { GroupEntityPage } from './GroupEntityPage';
import { UserEntityPage } from './UserEntityPage';
import { InfrastructureEntityPage } from './InfrastructureEntityPage';
import { RepositoryEntityPage } from './RepositoryEntityPage';
import { SystemRoleEntityPage } from './SystemRoleEntityPage';
import { GenericEntityPage } from './GenericEntityPage';
import { NET_PATH } from '../catalog/netPath';

const useNotFoundStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    root: {
      backgroundColor: c.background.page,
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
    },
    netBackground: {
      position: 'absolute',
      top: -469,
      left: 217,
      width: 1141,
      height: 1054,
      pointerEvents: 'none',
      zIndex: 0,
    },
    content: {
      position: 'relative',
      zIndex: 1,
      padding: 24,
      maxWidth: 1500,
      margin: '0 auto',
    },
    card: {
      background: c.background.paper,
      borderRadius: 12,
      border: `1px solid ${c.border.default}`,
      padding: '48px 32px',
      textAlign: 'center',
      marginTop: 24,
    },
    title: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '1.25rem',
      fontWeight: 700,
      color: c.text.primary,
      margin: '0 0 8px 0',
      userSelect: 'none',
    },
    description: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.9rem',
      color: fgAlpha(0.6),
      margin: '0 0 24px 0',
      lineHeight: 1.6,
    },
    entityRef: {
      fontFamily: 'monospace',
      fontSize: '0.85rem',
      color: fgAlpha(0.5),
      background: fgAlpha(0.05),
      borderRadius: 4,
      padding: '2px 8px',
    },
    link: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.85rem',
      fontWeight: 500,
      color: c.text.link,
      cursor: 'pointer',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  };
});

const EntityNotFound = () => {
  const { classes } = useNotFoundStyles();
  const params = useParams<{ namespace: string; kind: string; name: string }>();
  const entityRef = [params.namespace, params.kind, params.name]
    .filter(Boolean)
    .join('/');

  return (
    <div className={classes.root}>
      <svg
        className={classes.netBackground}
        viewBox="0 0 1378 500"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="netFadeEntityDetails" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="white" stopOpacity="1" />
            <stop offset="70%" stopColor="white" stopOpacity="0.6" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <mask id="netMaskEntityDetails">
            <rect
              width="100%"
              height="100%"
              fill="url(#netFadeEntityDetails)"
            />
          </mask>
        </defs>
        <path
          d={NET_PATH}
          stroke="#B0B0B0"
          strokeOpacity="0.8"
          strokeWidth="0.5"
          strokeMiterlimit="10"
          strokeDasharray="0.5 2"
          mask="url(#netMaskEntityDetails)"
        />
      </svg>
      <div className={classes.content}>
        <PageBreadcrumbs
          segments={[
            { label: 'Catalog', to: '/catalog' },
            { label: 'Not Found' },
          ]}
        />
        <div className={classes.card}>
          <p className={classes.title}>Entity not found</p>
          <p className={classes.description}>
            The entity <span className={classes.entityRef}>{entityRef}</span>{' '}
            does not exist in the catalog.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Dispatches to a kind-specific detail page based on entity.kind.
 * Falls back to GenericEntityPage for unrecognised kinds.
 *
 * Kind → page mapping:
 *   system        → SystemDetailsPage       (Components, APIs, Infrastructure, Resources, Diagram, …)
 *   api           → ApiEntityPage           (Definition, Provided By, Consumed By, Feedback)
 *   component     → ComponentEntityPage     (Provided APIs, Consumed APIs, CI/CD, …)
 *   domain        → DomainEntityPage        (Systems, Diagram)
 *   group         → GroupEntityPage         (Members, Pull Requests, Diagram)
 *   user          → UserEntityPage          (Diagram)
 *   infrastructure → InfrastructureEntityPage (Resources)
 *   repository    → RepositoryEntityPage    (AppStandardInsights, Docs)
 *   academy       → AcademyEntityPage       (Docs, Codelab for tutorials)
 *   content       → ContentEntityPage       (Content, Docs, Feedback)
 *   systemrole    → SystemRoleEntityPage    (Feedback)
 *   * (all others) → GenericEntityPage      (Overview + Relations)
 *     – covers: GitHub Action, Location, Resource, Seamless, Template
 */
export const EntityDetailsPage = () => {
  const { entity, loading } = useAsyncEntity();

  if (loading) return null;
  if (!entity) return <EntityNotFound />;

  switch (entity.kind.toLowerCase()) {
    case 'system':
      return <SystemDetailsPage />;
    case 'api':
      return <ApiEntityPage />;
    case 'component':
      return <ComponentEntityPage />;
    case 'domain':
      return <DomainEntityPage />;
    case 'group':
      return <GroupEntityPage />;
    case 'user':
      return <UserEntityPage />;
    case 'infrastructure':
      return <InfrastructureEntityPage />;
    case 'repository':
      return <RepositoryEntityPage />;
    case 'academy':
      return <AcademyEntityPage />;
    case 'content':
      return <ContentEntityPage />;
    case 'systemrole':
      return <SystemRoleEntityPage />;
    default:
      return <GenericEntityPage />;
  }
};
